import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Card,
    CardMedia,
    CardContent,
    Alert,
    Grid
} from '@mui/material';
import { getBackendUserUrl } from '../../utils/utils';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const MAX_DESC_LENGTH = 150;

const ServiceAutofillForm = ({ serviceType, onServiceSelected }) => {
    const [serviceName, setServiceName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [address, setAddress] = useState('');
    const [contact, setContact] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFullDesc, setShowFullDesc] = useState(false);

    const handleAutofill = async () => {
        if (!serviceName.trim()) {
            setError('Please enter a service name');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            console.log("serviceName", serviceName);
            console.log("serviceType", serviceType);
            // First try to search in our database
            const searchUrl = `${getBackendUserUrl()}/company/search?query=${encodeURIComponent(serviceName.trim())}`;
            console.log("Searching URL:", searchUrl);
            
            const searchResponse = await axios.get(searchUrl);
            console.log("searchResponse", searchResponse);

            if (searchResponse.data && searchResponse.data.length > 0) {
                const serviceData = searchResponse.data[0];
                console.log("found the service in DB: serviceData", serviceData);
                setPreviewData({
                    Company: {
                        ...serviceData,
                        serviceType: serviceType
                    }
                });
                setShowFullDesc(false);
                return;
            }

            // If not found in database, try web search
            const response = await axios.post(getBackendUserUrl() + '/autofill_company/autofill_service_details', {
                name: serviceName.trim(),
                website: websiteUrl.trim(),
                address: address.trim(),
                service_type: serviceType,
                country: 'United States'
            });
            console.log("response", response);
            if (response.data) {
                console.log("previewData", response.data);
                setPreviewData(response.data);
                setShowFullDesc(false);
            }
        } catch (err) {
            console.error("Autofill error:", err);
            console.error("Error response:", err.response);
            
            let userMessage = 'No services found. Please try a more specific name.';
            
            if (err.response) {
                console.error("Error status:", err.response.status);
                console.error("Error data:", err.response.data);
                
                if (err.response.status === 400) {
                    userMessage = 'Invalid search request. Please check your input and try again.';
                } else if (err.response.status === 404) {
                    userMessage = 'No services found. Please check your spelling or try a different name.';
                } else if (err.response.status === 500) {
                    userMessage = 'Server error occurred. Please try again later.';
                } else {
                    userMessage = `Search failed (${err.response.status}). Please try again.`;
                }
            } else if (err.request) {
                console.error("Network error:", err.request);
                userMessage = 'Network error. Please check your connection and try again.';
            } else {
                console.error("Other error:", err.message);
                userMessage = 'An unexpected error occurred. Please try again.';
            }
            
            setError(userMessage);
            setPreviewData(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleServiceNameChange = (e) => {
        setServiceName(e.target.value);
        if (error) setError('');
    };

    const handleConfirm = () => {
        console.log("previewData", previewData);
        if (previewData && onServiceSelected) {
            // Format the data to match the cartAddons structure
            const formattedData = {
                addonId: previewData.Company?.id || uuidv4(), // Generate new ID if not exists
                created: new Date().toISOString(),
                addonType: serviceType,
                desc: previewData.Company?.desc || '',
                price: previewData.Company?.price || 0,
                venueId: previewData.Company?.venueId || '',
                companyName: previewData.Company?.name || '',
                count: 0,
                companyId: previewData.Company?.id || '',
                status: 'pending',
                selected: true,
                isAutofilled: true, // New field to track autofilled items
                company: {
                    companyId: previewData.Company?.id || '',
                    name: previewData.Company?.name || '',
                    industry: serviceType,
                    desc: previewData.Company?.desc || '',
                    location: previewData.Company?.location || '',
                    logoUrl: previewData.Company?.logoUrl || '',
                    website: previewData.Company?.website || '',
                },
                package: previewData.Company?.packages || []
            };

            onServiceSelected(formattedData);
            setPreviewData(null);
            setServiceName('');
            setWebsiteUrl('');
            setAddress('');
            setContact('');
            setShowFullDesc(false);
        }
    };

    const formatAddress = (address) => {
        if (!address) return null;
        return address.split(',').map((line, idx) => (
            <span key={idx} style={{ display: 'block' }}>{line.trim()}</span>
        ));
    };

    const getDescription = () => {
        const desc = previewData?.Company?.desc || '';
        if (desc.length <= MAX_DESC_LENGTH) return desc;
        if (showFullDesc) return desc;
        return desc.slice(0, MAX_DESC_LENGTH) + '...';
    };

    return (
        <Grid container spacing={2} marginTop={5}>
            <Grid item xs={12} md={5}>
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Already have a {serviceType} booked?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Paste the {serviceType} name and website below, and we'll autofill it for you.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label={`${serviceType.charAt(0).toUpperCase() + serviceType.slice(1)} Name`}
                            value={serviceName}
                            onChange={handleServiceNameChange}
                            placeholder={`e.g., ${serviceType} Company Name`}
                            error={!!error}
                            helperText={error}
                        />
                        <TextField
                            fullWidth
                            label="Website URL"
                            value={websiteUrl}
                            onChange={(e) => setWebsiteUrl(e.target.value)}
                            placeholder="https://..."
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="e.g. 123 Main St, City, State"
                            error={!!error}
                            helperText={error}
                        />
                        <TextField
                            fullWidth
                            label="Point of Contact"
                            value={contact}
                            onChange={(e) => setContact(e.target.value)}
                            placeholder="contact@company.com"
                        />
                    </Box>

                    <Button
                        variant="contained"
                        onClick={handleAutofill}
                        disabled={isLoading}
                        sx={{ width: '100%' }}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            `Autofill & Add ${serviceType.charAt(0).toUpperCase() + serviceType.slice(1)}`
                        )}
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12} md={5}>
                {previewData && (
                    <Card sx={{ width: '100%', minWidth: 300, maxWidth: 500, mx: 'auto', p: 2, boxShadow: 3 }}>
                        {previewData.Company?.logoUrl ? (
                            <CardMedia
                                component="img"
                                height="180"
                                image={previewData.Company.logoUrl}
                                alt={previewData.Company.name}
                                sx={{ objectFit: 'contain', borderRadius: 2, mb: 1, bgcolor: '#f5f5f5' }}
                            />
                        ) : (
                            <Box sx={{ height: 180, bgcolor: '#f5f5f5', borderRadius: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'grey.500', fontSize: 18 }}>
                                No Logo Available
                            </Box>
                        )}
                        <CardContent sx={{ p: 0 }}>
                            <Typography variant="h6" gutterBottom>
                                {previewData.Company?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                {previewData.Company?.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {getDescription()}
                                {((previewData?.Company?.desc || '').length > MAX_DESC_LENGTH) && !showFullDesc && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        sx={{ ml: 1, p: 0, minWidth: 0, fontSize: 13, color: 'primary.main', textTransform: 'none', background: 'none', boxShadow: 'none', border: 'none', '&:hover': { background: 'none', textDecoration: 'underline' } }}
                                        onClick={e => { e.stopPropagation(); setShowFullDesc(true); }}
                                    >
                                        Read more
                                    </Button>
                                )}
                                {((previewData?.Company?.desc || '').length > MAX_DESC_LENGTH) && showFullDesc && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        sx={{ ml: 1, p: 0, minWidth: 0, fontSize: 13, color: 'primary.main', textTransform: 'none', background: 'none', boxShadow: 'none', border: 'none', '&:hover': { background: 'none', textDecoration: 'underline' } }}
                                        onClick={e => { e.stopPropagation(); setShowFullDesc(false); }}
                                    >
                                        Show less
                                    </Button>
                                )}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                onClick={handleConfirm}
                                sx={{ width: '100%', mt: 2 }}
                            >
                                Add this {serviceType} to your event
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </Grid>
        </Grid>
    );
};

export default ServiceAutofillForm; 